import { ArrowRight } from "lucide-react";

const HERO_IMAGE =
  "https://images.pexels.com/photos/10988763/pexels-photo-10988763.jpeg";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end overflow-hidden bg-ags-cream aaa-noise"
    >
      {/* Background image */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* Editorial duotone overlay — institutional, not a "gradient" */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(248,247,245,0.0) 0%, rgba(248,247,245,0.55) 55%, rgba(248,247,245,0.98) 100%)",
        }}
      />

      {/* Subtle navy tint to add brand cohesion */}
      <div aria-hidden className="absolute inset-0 bg-[#0E2A57]/[0.04] mix-blend-multiply" />

      {/* Section index — top-left editorial marker */}
      <div className="absolute top-32 md:top-36 left-6 md:left-12 z-10 hidden md:flex items-center gap-3">
        <span className="font-mono text-[11px] tracking-[0.22em] text-[#0E2A57]/70 uppercase">
          AGS · 01 / 12
        </span>
        <span className="h-px w-10 bg-[#0E2A57]/20" />
        <span className="font-mono text-[11px] tracking-[0.22em] text-[#0E2A57]/70 uppercase">
          Family-Owned Commercial Cleaning · Ireland
        </span>
      </div>

      <div className="relative container-ags pb-20 md:pb-28 pt-40 w-full">
        <div className="md:hidden flex items-center gap-3 mb-8 fade-up">
          <span className="h-px w-16 bg-[#2BB6E5]" />
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
            Family-Owned · Ireland
          </span>
        </div>

        <h1
          data-testid="hero-heading"
          className="font-heading font-extrabold text-[#050B14] text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.92] tracking-[-0.04em] max-w-5xl fade-up"
        >
          Across Ireland.
          <br />
          <span className="italic-display font-normal text-[#0E2A57]">
            Cleaning standards
          </span>{" "}
          with
          <br />
          family values.
        </h1>

        <div className="mt-12 grid md:grid-cols-12 gap-10 items-end">
          <p
            data-testid="hero-subtext"
            className="md:col-span-6 text-[#475569] text-lg md:text-2xl leading-[1.55] max-w-xl"
          >
            At AGS Cleaning Services, we provide professional commercial
            cleaning and facility services across Ireland. As a family-owned
            business, we combine reliable service, trained staff, and personal
            accountability to help businesses maintain clean, safe, and
            professional environments.
          </p>

          <div className="md:col-span-6 flex flex-wrap items-center gap-4 md:justify-end">
            <a
              href="#quote"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-[#0E2A57] text-white hover:bg-[#050B14] px-10 py-5 font-semibold tracking-wide transition-colors duration-300"
            >
              Request a Quote
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-3 bg-transparent border border-[#0E2A57] text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-10 py-5 font-semibold tracking-wide transition-all duration-300"
            >
              Explore Services
            </a>
          </div>
        </div>

        {/* KPI strip — hairline dividers, mono labels, compact */}
        <div className="mt-12 md:mt-16 pt-6 border-t border-ags-hairline-strong grid grid-cols-3 gap-px bg-ags-hairline-strong">
          {[
            { k: "€12.5M", v: "Public Liability Insurance" },
            { k: "24h",    v: "Quotation Turnaround" },
            { k: "100%",   v: "Eco-Friendly Approach" },
          ].map((s, i) => (
            <div
              key={s.k}
              data-testid={`hero-stat-${s.k}`}
              className="bg-ags-cream px-2 md:px-4 py-4 flex flex-col transition-colors hover:bg-ags-sand-soft"
            >
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#0E2A57]/55">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="mt-2 font-heading font-extrabold text-xl md:text-2xl text-ags-ink tracking-[-0.04em] leading-none">
                {s.k}
              </span>
              <span className="mt-2 text-[11px] md:text-xs text-ags-text-muted leading-snug">{s.v}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
